import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { Button, Flex, Form, Input, Rate, Typography } from "antd";
import { useEffect, useState } from "react";
import { RATING_MESSAGE_MAX_LENGTH } from "../../../common/constants/constants";
import { ticketActions } from "../../../features/ticket/ticketSlice";
import type { TicketRetrieve } from "../../generated-axios-client";

const RATING_COMMENT_INPUT_ID = "ticket-rating-comment";

type RatingPanelProps = {
  ticket: TicketRetrieve;
  dispatch: Dispatch;
  intl: IntlShape;
};

export const RatingPanel = ({ ticket, dispatch, intl }: RatingPanelProps) => {
  const savedMessage = ticket.rating_message ?? "";
  const [draftMessage, setDraftMessage] = useState(savedMessage);

  // Resync only when the saved value changes, so the refetch after a star click
  // does not wipe a comment that is still being typed
  useEffect(() => {
    setDraftMessage(savedMessage);
  }, [savedMessage]);

  const ticketId = ticket.id;
  if (!ticketId) return null;

  const changeRating = (value: number) => {
    if (value < 1) return;
    dispatch(ticketActions.setRating({ id: ticketId, value }));
  };

  const saveMessage = () => {
    if (!ticket.rating) return;
    dispatch(
      ticketActions.setRating({
        id: ticketId,
        value: ticket.rating,
        message: draftMessage.trim() || null,
      })
    );
  };

  const messageChanged = draftMessage.trim() !== savedMessage.trim();

  return (
    <Flex vertical gap={12}>
      <Flex align="center" gap={12}>
        <Typography.Text type="secondary">
          {intl.formatMessage({ id: "ticket_rating_label" })}
        </Typography.Text>
        <Rate value={ticket.rating ?? 0} onChange={changeRating} />
        {ticket.rating ? (
          <Typography.Text type="secondary">({ticket.rating}/5)</Typography.Text>
        ) : null}
      </Flex>
      <Form layout="vertical">
        {/* Keeps the default Form.Item bottom margin: the showCount counter renders
            below the TextArea and needs that room, or it sits on the Save button. */}
        <Form.Item
          label={intl.formatMessage({ id: "ticket_rating_comment_label" })}
          htmlFor={RATING_COMMENT_INPUT_ID}
        >
          <Input.TextArea
            id={RATING_COMMENT_INPUT_ID}
            rows={2}
            maxLength={RATING_MESSAGE_MAX_LENGTH}
            showCount
            disabled={!ticket.rating}
            placeholder={intl.formatMessage({
              id: ticket.rating
                ? "ticket_rating_comment_placeholder"
                : "ticket_rating_comment_requires_rating",
            })}
            value={draftMessage}
            onChange={(event) => setDraftMessage(event.target.value)}
          />
        </Form.Item>
      </Form>
      <Flex justify="flex-end">
        <Button disabled={!ticket.rating || !messageChanged} onClick={saveMessage}>
          {intl.formatMessage({ id: "ticket_rating_comment_save" })}
        </Button>
      </Flex>
    </Flex>
  );
};
